/**
 * Detalle de una carga (`/activa/oficial/base/cargas/:id`): qué se importó, con qué consentimiento, qué
 * columnas, qué etiquetas y qué errores, y la zona de peligro (SPEC-BD §6.2, B-13).
 *
 * - Importando o borrando: se refresca sola cada 3 s.
 * - «Borrar los datos de esta carga» borra de verdad los contactos que sólo vinieron de este archivo; el
 *   modal dice, con las palabras de la ficha, qué queda de los «no» (una ficha mínima con el teléfono),
 *   que los retiros manuales van a No contactar de Activa (y lo que eso frena en Activa), y lo que
 *   pierden los que vinieron de otra carga (las etiquetas y el consentimiento que les dio este
 *   archivo). Pide motivo: queda en la auditoría.
 * - Un borrador, o una fallida que no escribió contactos, se descarta. Una fallida que ya escribió
 *   contactos no se puede descartar: se le borran los datos (`accionPeligroCarga`).
 */

import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Download, Loader2, Trash2 } from "lucide-react";
import type { CargaPublica } from "../../types.base";
import { baseApi } from "../../baseApi";
import { usePolling } from "../../hooks/usePolling";
import { accionPeligroCarga, labelDestino } from "../../logic/base";
import { nombreDeQuienPara } from "../../logic/formato";
import { ModalMotivo } from "../../components/ModalMotivo";
import { Tarjeta } from "../../components/Tarjeta";
import { fecha, fechaHora, num, plural } from "../../../campana-nueva/utils/format";
import {
  CajaError, ChipEstadoCarga, Esqueleto, R_CARGAS, R_CARGAS_LISTA, descargarErroresCarga, llamar, textoFallo,
} from "../../components/base/comun";
import { ProgresoCarga } from "../../components/base/ProgresoCarga";
import { useResumenAlTerminar } from "./BaseLayout";
import { useEsEquipoDeenex } from "../../../campana-nueva/hooks/useEsEquipoDeenex";

const TIPO_CAMPO: Record<string, string> = { texto: "texto", numero: "número", fecha: "fecha" };

export default function CargaDetallePage() {
  const equipo = useEsEquipoDeenex();
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [carga, setCarga] = useState<CargaPublica | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [noEncontrada, setNoEncontrada] = useState(false);

  const [modalBorrar, setModalBorrar] = useState(false);
  const [borrando, setBorrando] = useState(false);
  const [errorAccion, setErrorAccion] = useState("");
  const [descartando, setDescartando] = useState(false);
  const [descargando, setDescargando] = useState(false);
  const [errorDescarga, setErrorDescarga] = useState("");
  const [reintentando, setReintentando] = useState(false);
  const [errorReintento, setErrorReintento] = useState("");
  const [archivoVencido, setArchivoVencido] = useState(false);

  const cargar = useCallback(async (lanzar = false) => {
    const l = await llamar<CargaPublica>(() => baseApi.carga(id));
    setCargando(false);
    if (!l.ok || !l.data) {
      if (!l.ok && l.status === 404) { setNoEncontrada(true); setCarga(null); return; }
      setError(l.ok ? "El servidor no devolvió la carga." : textoFallo(l));
      if (lanzar) throw new Error("sin carga");
      return;
    }
    setCarga(l.data);
    setError("");
  }, [id]);

  useEffect(() => { setCargando(true); setNoEncontrada(false); void cargar(); }, [cargar]);

  const enCurso = carga?.estado === "importando" || carga?.estado === "eliminando";
  const poll = usePolling(() => cargar(true), enCurso ? 3000 : null, [id]);
  useResumenAlTerminar(carga?.estado);

  const borrarDatos = async (motivo: string) => {
    if (!carga) return;
    setBorrando(true);
    setErrorAccion("");
    const l = await llamar<CargaPublica>(() => baseApi.eliminarDatosCarga(carga.id, motivo));
    setBorrando(false);
    if (!l.ok || !l.data) { setErrorAccion(l.ok ? "El servidor no devolvió la carga." : textoFallo(l)); setModalBorrar(false); return; }
    setModalBorrar(false);
    setCarga(l.data);
  };

  const descartar = async () => {
    if (!carga || descartando) return;
    if (!window.confirm("¿Descartar esta carga? No se importa nada de este archivo.")) return;
    setDescartando(true);
    setErrorAccion("");
    const l = await llamar<{ descartada: true }>(() => baseApi.descartarCarga(carga.id));
    setDescartando(false);
    if (!l.ok) { setErrorAccion(textoFallo(l)); return; }
    navigate(R_CARGAS_LISTA, { replace: true });
  };

  const reintentar = async () => {
    if (!carga || reintentando) return;
    setReintentando(true);
    setErrorReintento("");
    const l = await llamar<CargaPublica>(() => baseApi.importar(carga.id, carga.huellaPrevia || ""));
    setReintentando(false);
    if (!l.ok || !l.data) {
      if (!l.ok && l.code === "ARCHIVO_VENCIDO") { setArchivoVencido(true); return; }
      setErrorReintento(l.ok ? "El servidor no devolvió la carga." : textoFallo(l));
      return;
    }
    setCarga(l.data);
  };

  const descargarErrores = async () => {
    if (!carga || descargando) return;
    setDescargando(true);
    setErrorDescarga(await descargarErroresCarga(carga.id, carga.nombre));
    setDescargando(false);
  };

  const volver = (
    <Link to={R_CARGAS_LISTA} className="text-xs font-semibold text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
      <ArrowLeft size={13} /> Contactos con consentimiento
    </Link>
  );

  if (noEncontrada) {
    return (
      <div className="space-y-4">
        {volver}
        <div className="rounded-2xl border border-slate-100 bg-white p-8 text-center shadow-sm">
          <p className="text-sm text-slate-600">No encontramos esta carga (puede que se haya descartado o que haya vencido sin importarse).</p>
          <Link to={R_CARGAS_LISTA} className="mt-4 h-10 px-4 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 inline-flex items-center">
            Volver a las cargas
          </Link>
        </div>
      </div>
    );
  }
  if (cargando && !carga) return <div className="space-y-4">{volver}<Esqueleto filas={4} alto="h-28" /></div>;
  if (!carga) {
    return <div className="space-y-4">{volver}<CajaError titulo="No pudimos traer la carga." detalle={error} onReintentar={() => { setCargando(true); void cargar(); }} /></div>;
  }

  const r = carga.resultado;
  const re = carga.resultadoEliminacion;
  const peligro = accionPeligroCarga(carga);

  return (
    <div className="space-y-5">
      {volver}

      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-semibold text-slate-900 tracking-tight">{carga.nombre}</h3>
            <ChipEstadoCarga estado={carga.estado} progreso={carga.progreso} />
          </div>
          {/* Quién la subió (y abajo, quién declaró el consentimiento) dicho como persona con `nombreDeQuien`: se veía el
              email del token tal cual, «alan vía hatsu-demo@operador.deenex». */}
          <p className="text-sm text-slate-500 mt-0.5">
            {carga.nombreArchivo} · {fechaHora(carga.importadaAt || carga.createdAt)} · {nombreDeQuienPara(carga.creadoPor?.email, equipo) || "—"}
          </p>
        </div>
      </header>

      {error && <p className="text-xs text-amber-700">{error} Se muestra lo último que trajimos.</p>}
      {poll.detenido && (
        <div className="flex items-center justify-between gap-3 rounded-xl bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
          <span>Dejamos de actualizar porque el servidor no responde.</span>
          <button type="button" onClick={poll.reintentar} className="font-semibold underline">Reintentar</button>
        </div>
      )}
      {errorAccion && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">{errorAccion}</p>}

      {carga.estado === "borrador" && (
        <div className="flex items-center justify-between gap-3 flex-wrap rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-700">
          <span>Esta carga todavía no se importó.</span>
          {equipo && <Link to={`${R_CARGAS}/nueva?carga=${encodeURIComponent(carga.id)}`} className="font-semibold text-violet-700 underline">Seguir con la carga</Link>}
        </div>
      )}

      {(carga.estado === "importando" || carga.estado === "fallida") && (
        <Tarjeta titulo="Importación">
          <ProgresoCarga carga={carga} onReintentar={reintentar} reintentando={reintentando} errorReintento={errorReintento}
            archivoVencido={archivoVencido} onDescargarErrores={descargarErrores} descargando={descargando} errorDescarga={errorDescarga}
            onSubirOtro={() => navigate(`${R_CARGAS}/nueva`)} soloLectura={!equipo} />
        </Tarjeta>
      )}

      {carga.estado === "eliminando" && (
        <p className="flex items-center gap-2 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800"><Loader2 size={14} className="animate-spin" /> Borrando los datos de esta carga…</p>
      )}
      {carga.estado === "eliminada" && re && re.noBorrados > 0 && (
        <div className="flex items-center justify-between gap-3 flex-wrap rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <span className="flex items-center gap-2"><AlertTriangle size={15} /> {num(re.noBorrados)} {re.noBorrados === 1 ? "contacto tenía un mensaje saliendo y no se borró." : "contactos tenían un mensaje saliendo y no se borraron."}</span>
          {equipo && <button type="button" onClick={() => setModalBorrar(true)} className="font-semibold underline">Reintentar el borrado</button>}
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-2">
        <Tarjeta titulo="Resultado">
          {r ? (
            <dl className="grid grid-cols-2 gap-3 text-sm">
              <Dato label="Filas leídas" valor={num(carga.filas)} />
              <Dato label="Contactos nuevos" valor={num(r.nuevos)} />
              <Dato label="Actualizados" valor={num(r.actualizados)} />
              <Dato label="Sin cambios" valor={num(r.sinCambios)} />
              <Dato label="Repetidos en el archivo" valor={num(r.duplicadosEnArchivo)} />
              <Dato label="Teléfonos inválidos" valor={num(r.telefonosInvalidos)} />
              <Dato label="Con consentimiento" valor={num(r.conConsentimiento)} />
              <Dato label="Dijeron que no" valor={num(r.dijeronQueNo)} />
              <Dato label="En no contactar" valor={num(r.enNoContactar)} />
              <Dato label="Siguen en la base" valor={num(carga.contactosVigentes)} />
            </dl>
          ) : (
            <p className="text-sm text-slate-500">{carga.estado === "borrador" ? "Todavía no se importó." : "Sin resultado todavía."} Filas del archivo: {num(carga.filas)}.</p>
          )}
          {re && (
            <p className="text-xs text-slate-500 mt-3">
              Datos borrados el {fecha(carga.eliminadaAt || undefined)}: {num(re.suprimidos)} {plural(re.suprimidos, "contacto borrado", "contactos borrados")}, {num(re.desvinculados)} {plural(re.desvinculados, "desvinculado", "desvinculados")} de esta carga.
            </p>
          )}
        </Tarjeta>

        <Tarjeta titulo="Consentimiento">
          {carga.declaracion ? (
            <div className="text-sm text-slate-700 space-y-1">
              <p>Se declaró que todo el archivo tiene consentimiento.</p>
              <p><span className="text-slate-500">Cómo:</span> {carga.declaracion.fuente}</p>
              <p><span className="text-slate-500">Cuándo:</span> {fecha(`${carga.declaracion.fecha}T12:00:00`)}</p>
              <p className="text-xs text-slate-500">Lo declaró {nombreDeQuienPara(carga.declaracion.por?.email, equipo) || "—"} el {fechaHora(carga.declaracion.at)}.</p>
            </div>
          ) : (
            <p className="text-sm text-slate-600">Sin declaración: cuenta la columna de cada fila</p>
          )}
        </Tarjeta>

        <Tarjeta titulo="Columnas">
          {carga.vinculo?.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  <tr><th className="py-1.5 pr-3">Columna del archivo</th><th className="py-1.5">Qué es</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {carga.vinculo.map((v) => (
                    <tr key={v.columna}>
                      <td className="py-1.5 pr-3 text-slate-700">{v.columna.startsWith("__EMPTY") ? "(columna sin nombre)" : v.columna}</td>
                      <td className={`py-1.5 ${v.destino === "ignorar" ? "text-slate-400" : "text-slate-900"}`}>
                        {v.destino === "campo" && v.campo ? `${v.campo.etiqueta} (${TIPO_CAMPO[v.campo.tipo] ?? v.campo.tipo})` : labelDestino(v.destino)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-slate-500">Todavía no se vincularon las columnas.</p>
          )}
        </Tarjeta>

        <Tarjeta titulo="Etiquetas de la carga">
          {carga.etiquetas?.length ? (
            <div className="flex flex-wrap gap-1.5">
              {carga.etiquetas.map((e) => <span key={e.clave} className="rounded-full bg-violet-50 text-violet-700 text-xs font-semibold px-2.5 py-0.5">{e.texto}</span>)}
            </div>
          ) : <p className="text-sm text-slate-500">Sin etiquetas.</p>}
        </Tarjeta>

        <Tarjeta titulo="Errores">
          <p className="text-sm text-slate-700">
            {carga.erroresTotal ? `${num(carga.erroresTotal)} ${carga.erroresTotal === 1 ? "fila con problemas" : "filas con problemas"}` : "Ninguna fila con problemas."}
          </p>
          {carga.erroresTotal > 0 && (
            <button type="button" onClick={descargarErrores} disabled={descargando}
              className="mt-3 h-9 px-3 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5 disabled:opacity-50">
              {descargando ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Descargar el detalle (CSV)
            </button>
          )}
          {errorDescarga && <p className="text-xs text-red-600 mt-2" role="alert">{errorDescarga}</p>}
        </Tarjeta>
      </div>

      {/* Borrar o descartar una carga es del equipo de Deenex (Ley 25.326: lo ejecuta Deenex). */}
      {peligro && equipo && (
        <section className="rounded-2xl border border-red-100 bg-white p-6 shadow-sm">
          <h4 className="text-sm font-semibold text-red-700">Zona de peligro</h4>
          {peligro === "borrar_datos" ? (
            <button type="button" onClick={() => setModalBorrar(true)}
              className="mt-3 h-10 px-4 rounded-xl border border-red-200 bg-white text-sm font-semibold text-red-700 hover:bg-red-50 inline-flex items-center gap-2">
              <Trash2 size={15} /> Borrar los datos de esta carga
            </button>
          ) : (
            <button type="button" onClick={descartar} disabled={descartando}
              className="mt-3 h-10 px-4 rounded-xl border border-red-200 bg-white text-sm font-semibold text-red-700 hover:bg-red-50 inline-flex items-center gap-2 disabled:opacity-50">
              {descartando ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />} Descartar esta carga
            </button>
          )}
        </section>
      )}

      {modalBorrar && (
        <ModalMotivo
          titulo="Borrar los datos de esta carga"
          descripcion={`${carga.estado === "fallida" ? "Esta carga falló a mitad, pero ya importó una parte. " : ""}Se borran de verdad los contactos que sólo vinieron de este archivo (esta carga tiene ${num(carga.contactosVigentes)} en total), y sus mensajes quedan anónimos, también su conversación en Conversaciones y los archivos que mandó, que se borran también del proveedor de WhatsApp. De esos, los que habían dicho que no quedan como una ficha mínima: el teléfono con su «no», nada más (en Contactos se ven como «Sin nombre»), así ninguna carga los vuelve a sumar con un sí. A los que se les quitó el consentimiento a mano (retiro manual, con «Quitar consentimiento» en su ficha) se los da de baja (en Bajas) en vez de dejarles la ficha: dejan de recibir también las campañas por QR y sus mails, y esa baja se levanta de a una, con su pedido por escrito. Si su número tiene una baja levantada, no se reactiva: quedan como ficha mínima. Los contactos que también vinieron de otra carga se quedan, pero pierden las etiquetas de esta carga (aunque otra carga también se las haya puesto) y el consentimiento que les dio este archivo: si no tienen otro, quedan sin consentimiento. Las bajas que ya están en Bajas no se borran. No se puede deshacer.`}
          confirmar="Borrar los datos"
          peligro
          busy={borrando}
          onConfirmar={borrarDatos}
          onCancelar={() => setModalBorrar(false)}
        />
      )}
    </div>
  );
}

function Dato({ label, valor }: { label: string; valor: string }) {
  return (
    <div>
      <dt className="text-[11px] text-slate-500">{label}</dt>
      <dd className="text-base font-semibold text-slate-900">{valor}</dd>
    </div>
  );
}
